import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { safeSetInspectionItem } from '../utils/inspectionStorage';
import './InspectionChecklist.css';

const checklistGroups = [
  {
    name: 'Test drive',
    items: ['Engine Performance', 'Road Handling', 'Braking', 'Steering / Alignment', 'Transmission Shifting', 'Shocks / Suspension', 'Road Noise', 'Tire Balance'],
  },
  {
    name: 'Exterior Inspection',
    items: ['Paint Finish', 'Body Damage', 'Rust', 'Windshield / Glass', 'Headlights / Turn Signals', 'Mirrors', 'Bumpers', 'Underbody'],
  },
  {
    name: 'Electrical System',
    items: ['Interior Lighting', 'Battery', 'Instrument Gauges', 'Air Conditioning', 'Heater Operation', 'Stereo System', 'Wiper System', 'Power Seats', 'OBD2 Code Scan'],
  },
  {
    name: 'Under The Hood',
    items: ['Fluid Levels', 'Hoses', 'Belts', 'Air Filter', 'Radiator', 'Battery', 'Oil Leaks', 'Engine Noise'],
  },
  {
    name: 'Interior Inspection',
    items: ['Upholstery', 'Interior Trim', 'Power Windows', 'Seats', 'Carpet / Mat', 'Instrument Panel', 'Headliner', 'Seatbelts'],
  },
  {
    name: 'Bumper To Bumper',
    items: ['Wheels', 'Tires', 'Trunk', 'Spare Tire / Jack', 'Exhaust System', 'Windows Operation', 'Warning Lights', 'Owners Manual'],
  },
  {
    name: 'Others',
    items: ['Additional Notes'],
  },
];

const makeKey = (groupName, itemName) => `${groupName}::${itemName}`;

function InspectionChecklist({ onLogout }) {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const inspectionIdFromUrl = urlParams.get('id');
  const sessionId = localStorage.getItem('inspectionSessionId') || 'new';
  const storageKey = (inspectionIdFromUrl && inspectionIdFromUrl !== 'new') 
    ? `inspection_${inspectionIdFromUrl}_checklist` 
    : `inspection_${sessionId}_checklist`;

  const saved = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '{}');
    } catch (error) {
      return {};
    }
  }, [storageKey]);
  const [checks, setChecks] = useState(saved);

  const updateCheck = (groupName, itemName, field, value) => {
    const key = makeKey(groupName, itemName);
    setChecks((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        [field]: value,
      },
    }));
  };

  const saveChecklist = () => {
    safeSetInspectionItem(storageKey, JSON.stringify(checks));
    navigate(`/inspection/new?id=${inspectionIdFromUrl || sessionId}`);
  };

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="checklist-page">
        <header className="checklist-header">
          <button className="app-back-button checklist-back" type="button" onClick={() => navigate(`/inspection/new?id=${inspectionIdFromUrl || sessionId}`)} aria-label="Back to inspection">
            Back
          </button>
          <div>
            <h1>Checklist</h1>
            <p>Record condition for each inspection item.</p>
          </div>
        </header>

        <section className="checklist-card">
          <div className="checklist-title-row">
            <h2>Inspection Checklist</h2>
            <span>good / fair / poor</span>
          </div>

          <div className="checklist-table-wrap">
            <table className="checklist-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Good</th>
                  <th>Fair</th>
                  <th>Poor</th>
                  <th>Details</th>
                </tr>
              </thead>
              {checklistGroups.map((group) => (
                <tbody key={group.name}>
                  <tr className="checklist-group-row">
                    <td colSpan="5">{group.name}</td>
                  </tr>
                  {group.items.map((item) => {
                    const key = makeKey(group.name, item);
                    const value = checks[key] || {};

                    return (
                      <tr key={key}>
                        <td>{item}</td>
                        {['good', 'fair', 'poor'].map((status) => (
                          <td key={status}>
                            <label className={`checklist-radio checklist-radio-${status}`}>
                              <input
                                type="radio"
                                name={key}
                                checked={value.status === status}
                                onChange={() => updateCheck(group.name, item, 'status', status)}
                              />
                              <span />
                            </label>
                          </td>
                        ))}
                        <td>
                          <input
                            type="text"
                            value={value.details || ''}
                            onChange={(event) => updateCheck(group.name, item, 'details', event.target.value)}
                            placeholder="Add details"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        </section>

        <button className="checklist-save" type="button" onClick={saveChecklist}>Save</button>
      </main>
    </div>
  );
}

export default InspectionChecklist;
